import { useRef, useCallback } from 'react'
import { apiStream } from '@/shared/lib/apiClient'
import { useRedactorStore } from '../store/redactorStore'
import { composePrompt } from '../lib/promptComposer'

interface SSEEvent {
  type: 'token' | 'done' | 'error'
  value?: string
  inputTokens?: number
  outputTokens?: number
  message?: string
}

export function useRedact() {
  const abortRef = useRef<AbortController | null>(null)
  const {
    inputText,
    tone,
    audience,
    messageType,
    skillId,
    setStatus,
    appendOutputText,
    setOutputText,
    setError,
    setTokenUsage,
  } = useRedactorStore()

  const redact = useCallback(async (customSkillPrompt?: string) => {
    if (!inputText.trim()) return

    // Abort any in-flight request
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setOutputText('')
    setStatus('streaming')
    console.log('[Redact] Starting redaction', { tone, audience, messageType, inputLength: inputText.length })

    const { systemPrompt, userContent } = composePrompt({
      baseSkill: customSkillPrompt,
      tone,
      audience,
      messageType,
      userMessage: inputText,
    })

    try {
      const reader = await apiStream(
        '/redact',
        { systemPrompt, userContent, tone, audience, messageType, skillId },
        controller.signal
      )

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const raw = line.slice(6).trim()
          if (!raw) continue

          try {
            const event = JSON.parse(raw) as SSEEvent
            if (event.type === 'token' && event.value) {
              appendOutputText(event.value)
            } else if (event.type === 'done') {
              console.log('[Redact] Stream complete', { inputTokens: event.inputTokens, outputTokens: event.outputTokens })
              setStatus('done')
              if (event.inputTokens !== undefined && event.outputTokens !== undefined) {
                setTokenUsage(event.inputTokens, event.outputTokens)
              }
            } else if (event.type === 'error') {
              console.error('[Redact] Stream error', event.message)
              setError(event.message ?? 'Unknown error')
            }
          } catch {
            // Malformed SSE line — skip
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        console.log('[Redact] Request aborted by user')
        setStatus('aborted')
      } else {
        console.error('[Redact] Request failed', err)
        setError(err instanceof Error ? err.message : 'Request failed')
      }
    }
  }, [inputText, tone, audience, messageType, skillId, setStatus, setOutputText, appendOutputText, setError, setTokenUsage])

  const abort = useCallback(() => {
    abortRef.current?.abort()
    setStatus('aborted')
  }, [setStatus])

  return { redact, abort }
}
